import {
  BaseEdge,
  Handle,
  Position as HandlePosition,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import { cGradeOf } from '@shared/levels';
import type { ExecutiveTree, ExecutiveTreeNode } from '@shared/types';

/**
 * ผังผู้บริหาร — การ์ดคนและการวางผังแบบแถบต่อลำดับ
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ ทำไมไม่ใช้ treeLayout.ts ร่วมกับผังหน่วยงาน
 * ────────────────────────────────────────────────────────────────
 *
 * ไม่ใช่เพราะขี้เกียจใช้ของกลาง แต่เพราะเป็นการวางคนละแบบจริง ๆ
 *
 *   treeLayout   วางแบบต้นไม้ พี่น้องเรียงต่อกันบนแกนกระจาย ความกว้างของกิ่ง
 *                เป็นผลรวมของลูก ๆ — เหมาะกับผังหน่วยงานที่แต่ละชั้นมีลูกไม่กี่ตัว
 *   ที่นี่        วางเป็นแถบตามลำดับที่ผู้ใช้กำหนด และแถบหนึ่งตัดขึ้นบรรทัดใหม่ได้
 *
 * ลองใช้ treeLayout กับผังนี้แล้ว ลำดับ 2 (15 อัตรา) กับลำดับ 3 (8 อัตรา)
 * เป็นลูกของปลัดทั้งคู่ จึงถูกเรียงต่อกันบนแกนเดียวกันเป็น 23 ช่อง
 * ได้ผังกว้าง 6,320 สูง 632 (10:1) ซึ่งแบนจนอ่านไม่ออกแบบเดียวกับรอบก่อน
 * การตัดขึ้นบรรทัดใหม่ในแถบทำให้กว้างไม่เกิน 8 ใบเสมอ ไม่ว่าลำดับนั้นจะมีกี่คน
 *
 * ⚠ ห้ามเอาการตัดบรรทัดนี้ไปใส่ treeLayout เพื่อให้ "ใช้ตัวเดียวกัน"
 *   ผังหน่วยงานต้องการให้พ่ออยู่กึ่งกลางของลูกเสมอ ซึ่งขัดกับการตัดบรรทัด
 */

export type PersonNodeData = {
  node: ExecutiveTreeNode;
  dir: 'TB' | 'LR';
  onOpen: (posId: string) => void;
};

export type PersonFlowNode = Node<PersonNodeData, 'person'>;

/**
 * ขนาดจริงของการ์ดหนึ่งใบ
 *
 * ⚠ ต้องส่งออกไปให้ตัวเล็งกล้องใน OrgFlow ใช้ ไม่ใช่ให้มันเดาเอง
 *   ตัวเล็งกล้องต้องรู้ขอบขวาและขอบล่างของผัง ซึ่งคือพิกัดของโหนดสุดท้าย
 *   บวกขนาดการ์ด ถ้าเดาน้อยไป ผังจะถูกตัดขอบโดยไม่มีอะไรฟ้อง
 */
export const PERSON_CARD = { w: 248, h: 168 };

/**
 * ขนาดของช่องหนึ่งช่อง แยกตามทิศ
 *
 * ⚠ ต้องแยกตามทิศ ไม่ใช่ใช้ { w, h } ชุดเดียวแล้วสลับแกน
 *   การ์ดกว้าง 248 สูงไม่เกิน 168 (.pnode ใน styles.css คุมจำนวนบรรทัดไว้
 *   อย่างละสองบรรทัด ความสูงสูงสุดจึงคาดเดาได้) สองค่านี้ไม่เท่ากัน
 *   ช่องบนแกนไหนจึงต้องเทียบกับขนาดการ์ด "บนแกนนั้น"
 *
 *   TB  แกนกระจายคือแนวนอน ต้องกว้างกว่า 248 · แกนลึกคือแนวตั้ง ต้องสูงกว่า 168
 *   LR  สลับกัน แกนกระจายเป็นแนวตั้ง แกนลึกเป็นแนวนอน
 *
 * เคยตั้งชุดเดียวแล้วสลับแกน ผลคือแนวตั้งดูปกติดีทุกอย่าง แต่แนวนอนการ์ดทับกัน
 * 7 คู่ เพราะระยะระหว่างแถบ (200) น้อยกว่าความกว้างการ์ด (248)
 * สคริปต์ check:people-layout จับได้ ตาเปล่าไม่เห็นเพราะทับกันแค่ขอบ
 */
const CELL = {
  TB: { spread: 276, deep: 200 },
  LR: { spread: 200, deep: 284 },
};

/**
 * แนวคร่อมอยู่เหนือแถบของลำดับปลายทางเท่าไร
 *
 * ⚠ ต้องน้อยกว่า BAND_GAP เสมอ ไม่งั้นคร่อมจะไปทับการ์ดของลำดับก่อนหน้า
 */
const BRACKET_LIFT = 30;

/**
 * ช่องไฟระหว่างแถบของแต่ละลำดับ
 *
 * ⚠ ต้องมากพอให้ "ข้ามลำดับ" ดูต่างจาก "ขึ้นบรรทัดใหม่ในลำดับเดียวกัน"
 *   ลำดับ 2 มี 14 อัตรา จึงตัดเป็นสองแถว ถ้าช่องไฟระหว่างลำดับใกล้เคียงกับ
 *   ระยะระหว่างแถวในลำดับเดียวกัน ผู้ดูจะนับแถวบนจอแล้วเข้าใจว่ามีห้าลำดับ
 */
const BAND_GAP = 120;

/**
 * กว้างได้กี่ใบก่อนตัดขึ้นบรรทัดใหม่ — คำนวณต่อแถบ ไม่ใช่ค่าเดียวทั้งผัง
 *
 * ⚠ ค่าเดียวทั้งผังใช้ไม่ได้ ลำดับ 5 (ผู้อำนวยการสำนัก/กอง กับผู้เชี่ยวชาญ) มี 81 อัตรา
 *   ที่ 8 คอลัมน์จะกลายเป็น 9 แถว สูงจนผังยาวเกินกว่าจะย่อลงจอแล้วยังอ่านออก
 *   จึงยอมกว้างขึ้นเป็น 12 เมื่อแถบนั้นใหญ่ แลกกับแถวที่ไม่เกินหกแถว
 *
 * ⚠ แถบที่กริดกว้างไม่เท่ากันจะจัดช่องให้ตรงกับพ่อไม่ได้ (คอลัมน์ที่ 9 ของกริด 12
 *   ไม่ได้อยู่แนวเดียวกับคอลัมน์ที่ 1 ของกริด 8) โค้ดจึงตรวจก่อนว่ากริดเท่ากันไหม
 */
const COLS_BASE = 8;
const COLS_MAX = 12;
const ROWS_TARGET = 6;

function colsFor(count: number): number {
  return Math.min(COLS_MAX, Math.max(COLS_BASE, Math.ceil(count / ROWS_TARGET)));
}

export function PersonCardNode({ data }: NodeProps<PersonFlowNode>) {
  const n = data.node;
  const c = cGradeOf(n.level);
  const soon = n.retireInYears !== null && n.retireInYears <= 1;

  return (
    <div className={`pnode${n.vacant ? ' is-vacant' : ''}${n.unitHead ? ' is-head' : ''}`}>
      <Handle
        type="target"
        position={data.dir === 'TB' ? HandlePosition.Top : HandlePosition.Left}
        style={{ opacity: 0 }}
      />

      {/* ⚠ ทั้งใบกดได้ แต่ต้องเป็น <button> จริง ไม่ใช่ div ที่ผูก onClick
          ผังนี้ไล่ด้วยแป้น Tab ได้ และผู้ใช้หลักเป็นผู้บริหารที่ใช้คีย์บอร์ดเป็นหลัก */}
      <button type="button" className="pnode-hit" onClick={() => data.onOpen(n.posId)}>
        <span className="pnode-name">{n.vacant ? <i>ตำแหน่งว่าง</i> : n.name}</span>
        <span className="pnode-title">{n.title}</span>
        <span className="pnode-meta">
          <span className="pnode-lv">{n.level}</span>
          {c && <span className="pnode-c">{c}</span>}
        </span>
        {/* ⚠ หน้าที่ที่ได้รับมอบหมายใช้บรรทัดสังกัด ไม่เพิ่มบรรทัดใหม่ — การ์ดมีความสูงคงที่ในผัง
            ผู้ที่มีหน้าที่นี้ (รองปลัด · หัวหน้าผู้ตรวจราชการ) อยู่ สป.กค. ทั้งหมด สังกัดจึงไม่ได้บอกอะไรเพิ่ม
            ถ้าวันหนึ่งมีในกรมอื่นด้วย ต้องกลับมาคิดบรรทัดนี้ใหม่ */}
        <span className="pnode-unit" title={n.assignment ? `${n.assignment} · ${n.orgPath[0] ?? n.unit}` : undefined}>
          {n.assignment ?? n.orgPath[0] ?? n.unit}
        </span>

        {/* ⚠ ป้ายเกษียณกับป้ายว่างต้องอยู่บนการ์ด ไม่ใช่ให้กดเข้าไปดู
            คำถามแรกของปลัดเวลามองผังคือ "ตรงไหนกำลังจะว่าง" ถ้าต้องกดทีละใบก็ไม่ได้ตอบ */}
        {n.vacant ? (
          <span className="pnode-tag is-vacant">
            {n.vacantMonths === null ? 'ไม่ทราบว่าว่างตั้งแต่เมื่อไร' : `ว่างมาแล้ว ${duration(n.vacantMonths)}`}
          </span>
        ) : (
          soon && <span className="pnode-tag is-warn">เกษียณใน {retireIn(n.retireInYears!)}</span>
        )}
      </button>

      <Handle
        type="source"
        position={data.dir === 'TB' ? HandlePosition.Bottom : HandlePosition.Right}
        style={{ opacity: 0 }}
      />
    </div>
  );
}

export const personNodeTypes = { person: PersonCardNode };

/* ================================================================
   เส้นเชื่อมแบบฉาก
   ================================================================ */

/**
 * เส้นฉากแบบเดียวกับผังหน่วยงาน แต่หักมุมชิดแถวปลายทาง
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ ทำไมไม่ใช้ smoothstep ของ React Flow ตรง ๆ
 * ────────────────────────────────────────────────────────────────
 *
 * smoothstep วางแนวราบไว้ "กึ่งกลางระหว่างต้นทางกับปลายทาง" เสมอ
 * ผังหน่วยงานใช้ได้เพราะพ่อหนึ่งตัวมีลูกไม่กี่ตัวและอยู่แถวเดียว แนวราบจึงสั้น
 *
 * แต่ในผังนี้ปลัดมีลูก 23 ใบกระจายอยู่สามแถว (ลำดับ 2 สองแถว ลำดับ 3 อีกแถว)
 * แนวราบของทั้งสามกลุ่มจะไปกองอยู่ที่ระดับกลาง ๆ ใกล้กันหมด กลายเป็นรางยาว
 * พาดขวางทั้งผังแล้วแตกขาลงมา ซึ่งดูไม่ออกว่าเส้นไหนไปหาใคร
 *
 * หักมุมที่ "เหนือแถวปลายทาง" แทน แต่ละแถวจึงได้คร่อมของตัวเอง
 * สามกลุ่มได้สามคร่อมที่ระดับต่างกันชัดเจน ไม่ทับกัน
 *
 * ⚠ ฟังก์ชันนี้ถูกใช้สองที่ — คอมโพเนนต์เส้นจริง กับสคริปต์ตรวจหน้าตา
 *   ห้าม copy สูตรไปไว้ที่สคริปต์ ไม่งั้นภาพตรวจจะวาดคนละเส้นกับของจริง
 *   (เคยพลาดมาแล้ว ภาพตรวจวาดเส้นโค้งทั้งที่ของจริงเป็นเส้นฉาก)
 */
export function tierEdgePath(p: {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  dir: 'TB' | 'LR';
  /**
   * แนวคร่อมของแถบปลายทาง (พิกัดบนแกนลึก)
   *
   * ⚠ ต้องเป็นแนวของ "แถบทั้งลำดับ" ไม่ใช่ของแถวที่การ์ดนั้นอยู่
   *
   * ลำดับ 2 มี 15 อัตราซึ่งตัดเป็นสองแถว ถ้าคิดแนวคร่อมจากแถวของแต่ละใบ
   * จะได้คร่อมสองอันที่ระดับต่างกัน แต่ละอันมีเส้นแยกออกจากปลัดของตัวเอง
   * ผู้ใช้อ่านว่าเป็นคนละลำดับทันที ทั้งที่ทั้ง 15 คนอยู่ลำดับเดียวกัน
   *
   * ใช้แนวเดียวของทั้งแถบ ทุกใบในลำดับนั้นจึงห้อยจากคร่อมเส้นเดียวกัน
   * ใบที่อยู่แถวสองจะมีขาที่ยาวลงมาลอดหลังการ์ดแถวแรก (React Flow วาดเส้นไว้ใต้โหนด)
   */
  bracket?: number;
}): string {
  const R = 9;
  /** ระยะจากขอบการ์ดปลายทางถึงแนวหักมุม เมื่อผู้เรียกไม่ได้ส่งแนวคร่อมมา */
  const LIFT = 26;

  if (p.dir === 'LR') {
    const bx = p.bracket ?? p.targetX - LIFT;
    if (Math.abs(p.targetY - p.sourceY) < 2 || bx <= p.sourceX + R) {
      return `M ${p.sourceX},${p.sourceY} L ${p.targetX},${p.targetY}`;
    }
    const d = p.targetY > p.sourceY ? 1 : -1;
    return [
      `M ${p.sourceX},${p.sourceY}`,
      `L ${bx - R},${p.sourceY}`,
      `Q ${bx},${p.sourceY} ${bx},${p.sourceY + R * d}`,
      `L ${bx},${p.targetY - R * d}`,
      `Q ${bx},${p.targetY} ${bx + R},${p.targetY}`,
      `L ${p.targetX},${p.targetY}`,
    ].join(' ');
  }

  const by = p.bracket ?? p.targetY - LIFT;
  /* ตรงคอลัมน์กันพอดี หรือที่ว่างไม่พอหักมุม — ลากตรงไปเลย
     ⚠ ต้องมีข้อนี้ ไม่งั้นเส้นของคู่ที่อยู่ตรงกัน (อธิบดีกับรองอธิบดี)
       จะมีมุมโค้งเล็ก ๆ โผล่ออกมาข้างเดียว ซึ่งดูเหมือนเส้นเบี้ยว */
  if (Math.abs(p.targetX - p.sourceX) < 2 || by <= p.sourceY + R) {
    return `M ${p.sourceX},${p.sourceY} L ${p.targetX},${p.targetY}`;
  }
  const d = p.targetX > p.sourceX ? 1 : -1;
  return [
    `M ${p.sourceX},${p.sourceY}`,
    `L ${p.sourceX},${by - R}`,
    `Q ${p.sourceX},${by} ${p.sourceX + R * d},${by}`,
    `L ${p.targetX - R * d},${by}`,
    `Q ${p.targetX},${by} ${p.targetX},${by + R}`,
    `L ${p.targetX},${p.targetY}`,
  ].join(' ');
}

export function TierEdge({
  sourceX,
  sourceY,
  targetX,
  targetY,
  style,
  data,
}: EdgeProps<Edge<{ dir: 'TB' | 'LR'; bracket: number }>>) {
  return (
    <BaseEdge
      path={tierEdgePath({
        sourceX,
        sourceY,
        targetX,
        targetY,
        dir: data?.dir ?? 'TB',
        bracket: data?.bracket,
      })}
      style={style}
    />
  );
}

export const personEdgeTypes = { tier: TierEdge };

/**
 * วางผังเป็นแถบตามลำดับ
 *
 * แต่ละลำดับได้แถบของตัวเอง เรียงตามแกนลึก (TB = ลงล่าง · LR = ไปขวา)
 * ภายในแถบเรียงตามแกนกระจาย และตัดขึ้นแถวใหม่เมื่อเกิน MAX_COLS
 *
 * ⚠ ลำดับ 4 ต้องอยู่ "ช่องเดียวกับพ่อ" ไม่ใช่เรียงตามลำดับของตัวเอง
 *   รองอธิบดีทุกคนหน้าตาเหมือนกันหมดบนการ์ด (ชื่อตำแหน่งในทะเบียนคือ "นักบริหาร")
 *   สิ่งเดียวที่บอกว่าใครคู่กับใครคือตำแหน่งบนจอกับเส้นเชื่อม
 *   ถ้าไม่จัดให้ตรงช่องกัน เส้นจะไขว้กันไปมาจนอ่านไม่ออกว่ารองอธิบดีคนนี้ของกรมไหน
 */
export function buildPeopleGraph(
  tree: ExecutiveTree,
  dir: 'TB' | 'LR',
  onOpen: (posId: string) => void,
  /**
   * แสดงถึงลำดับไหน — ไม่ส่งมา = ทุกลำดับ
   *
   * ⚠ มีไว้เพราะผังทั้งหมดถึงซี 9 มี 114 อัตรา กว้าง 12 คอลัมน์ สูง 17 แถว
   *   ซึ่งบนจอจริงต้องย่อจนอ่านชื่อไม่ออก ค่าตั้งต้นของหน้าจอจึงแสดงถึงลำดับ 4
   *   (33 อัตรา) แล้วให้ผู้ใช้กดดูทั้งหมดเอง — ไม่ใช่ตัดลำดับ 5-6 ทิ้ง
   *   ยอดของทั้งสองแบบต้องขึ้นบนแถบให้เห็นเสมอว่ากำลังดูกี่อัตราจากทั้งหมดเท่าไร
   */
  maxTier?: number,
): { nodes: PersonFlowNode[]; edges: Edge[] } {
  const nodes: PersonFlowNode[] = [];
  const edges: Edge[] = [];
  if (!tree.root) return { nodes, edges };

  const cap = maxTier ?? Number.POSITIVE_INFINITY;

  /* ---------- รวบโหนดตามลำดับ ---------- */
  const byTier = new Map<number, ExecutiveTreeNode[]>();
  const parentOf = new Map<string, string>();
  (function walk(n: ExecutiveTreeNode) {
    if (n.tier > cap) return;
    byTier.set(n.tier, [...(byTier.get(n.tier) ?? []), n]);
    for (const c of n.children) {
      if (c.tier > cap) continue;
      parentOf.set(c.posId, n.posId);
      walk(c);
    }
  })(tree.root);

  const tiers = [...byTier.keys()].sort((a, b) => a - b);
  const cell = CELL[dir];
  /** posId -> ลำดับของโหนดนั้น ใช้ตอนเทียบว่าพ่ออยู่แถบที่กริดเท่ากันไหม */
  const tierByPos = new Map<string, number>();
  for (const [tier, list] of byTier) for (const n of list) tierByPos.set(n.posId, tier);
  const byTierOf = (posId: string) => tierByPos.get(posId);

  /* ---------- ช่องของแต่ละโหนด ---------- */
  /** posId -> คอลัมน์ที่อยู่ในแถบของตัวเอง */
  const colOf = new Map<string, number>();
  /** ลำดับ -> จำนวนคอลัมน์ของแถบนั้น */
  const colsOf = new Map<number, number>();
  /** ลำดับ -> จำนวนแถวที่แถบนั้นใช้ */
  const rowsOf = new Map<number, number>();

  for (const tier of tiers) {
    const list = byTier.get(tier)!;
    const cols = colsFor(list.length);
    colsOf.set(tier, cols);

    /* ---------- รอบแรก: ลูกคนเดียวไปอยู่ช่องเดียวกับพ่อ ----------
       ⚠ ทำเท่าที่ทำได้ ไม่ใช่ "ทำทั้งแถบหรือไม่ทำเลย"
         เดิมเขียนว่าถ้ามีสักโหนดที่จัดไม่ได้ก็เลิกจัดทั้งแถบ ผลคือพอลำดับ 4
         มีผู้ช่วยปลัดกระทรวง (ซึ่งพ่อคือปลัด ไม่ใช่อธิบดี) เพิ่มเข้ามาหนึ่งใบ
         รองอธิบดีทั้งแปดคนก็หลุดจากช่องของอธิบดีตัวเองไปด้วยทั้งแถว */
    const taken = new Set<number>();
    const rest: string[] = [];
    const kidsInBand = new Map<string, number>();
    for (const n of list) {
      const parent = parentOf.get(n.posId);
      if (parent) kidsInBand.set(parent, (kidsInBand.get(parent) ?? 0) + 1);
    }

    for (const n of list) {
      const parent = parentOf.get(n.posId);
      const parentCol = parent === undefined ? undefined : colOf.get(parent);
      const alone = parent !== undefined && kidsInBand.get(parent) === 1;
      /* จัดให้ตรงกันได้เฉพาะเมื่อพ่ออยู่แถบที่คอลัมน์กว้างเท่ากัน
         ไม่งั้นคอลัมน์ที่ 9 ของแถบกว้าง 12 จะไปตรงกับคอลัมน์ที่ 1 ของแถบกว้าง 8 */
      const sameGrid = parent !== undefined && colsOf.get(byTierOf(parent)!) === cols;
      if (alone && sameGrid && parentCol !== undefined && !taken.has(parentCol)) {
        colOf.set(n.posId, parentCol);
        taken.add(parentCol);
      } else {
        rest.push(n.posId);
      }
    }

    /* ---------- รอบสอง: ที่เหลือเติมช่องว่างจากซ้ายไปขวา ---------- */
    let next = 0;
    for (const id of rest) {
      while (taken.has(next)) next += 1;
      colOf.set(id, next);
      taken.add(next);
    }

    const maxCol = Math.max(0, ...list.map((n) => colOf.get(n.posId)!));
    rowsOf.set(tier, Math.floor(maxCol / cols) + 1);
  }

  /* ---------- แปลงเป็นพิกัด ---------- */
  /** ลำดับ -> ตำแหน่งเริ่มต้นของแถบบนแกนลึก */
  const bandStart = new Map<number, number>();
  let cursor = 0;
  for (const tier of tiers) {
    bandStart.set(tier, cursor);
    cursor += rowsOf.get(tier)! * cell.deep + BAND_GAP;
  }

  /* จัดกึ่งกลางแถบที่แคบกว่าแถบที่กว้างที่สุด ไม่งั้นแถบสั้น ๆ จะไปชิดซ้ายใบเดียว
     แล้วผังดูเหมือนเอียง ทั้งที่ตั้งใจให้ลำดับ 1 อยู่กลาง */
  const widest = Math.max(...tiers.map((t) => Math.min(byTier.get(t)!.length, colsOf.get(t)!)));
  const fullWidth = widest * cell.spread;

  for (const tier of tiers) {
    const list = byTier.get(tier)!;
    const cols = colsOf.get(tier)!;
    const used = Math.min(list.length, cols);
    /* ⚠ แถบที่ลูกไปอยู่ช่องเดียวกับพ่อ ห้ามจัดกึ่งกลางใหม่ ไม่งั้นจะเลื่อนหลุดจากพ่อ */
    const aligned = list.every((n) => parentOf.has(n.posId) && colOf.get(n.posId) === colOf.get(parentOf.get(n.posId)!));
    const offset = aligned ? 0 : (fullWidth - used * cell.spread) / 2;

    for (const n of list) {
      const col = colOf.get(n.posId)!;
      const spread = offset + (col % cols) * cell.spread;
      const deep = bandStart.get(tier)! + Math.floor(col / cols) * cell.deep;
      nodes.push({
        id: n.posId,
        type: 'person',
        position: dir === 'LR' ? { x: deep, y: spread } : { x: spread, y: deep },
        data: { node: n, dir, onOpen },
      });
    }
  }

  /**
   * เส้นเชื่อมมีสองแบบ และต้องดูต่างกัน
   *
   *   ทึบ   ต่อลงลำดับถัดไปทันที (ปลัด→ลำดับ 2/3 · อธิบดี→รองอธิบดี)
   *   ประ   ข้ามลำดับ (อธิบดี→ผู้อำนวยการ/ผู้เชี่ยวชาญ) ซึ่งแปลว่า
   *         "อยู่ส่วนราชการเดียวกัน" เท่านั้น ไม่ได้แปลว่าขึ้นตรงต่อกัน
   *
   * ⚠ ไม่ใช่เรื่องความสวยงาม — ลำดับ 5 มี 72 อัตราจาก 8 กรม เส้นจากอธิบดี
   *   ลงไปหาลูกของตัวเองจึงพาดข้ามกันทั้งแถบ ถ้าเส้นทั้งหมดหน้าตาเหมือนกัน
   *   ผังจะอ่านเหมือนสายบังคับบัญชาที่ยุ่งเหยิง ทั้งที่ข้อมูลไม่ได้บอกอะไรแบบนั้นเลย
   */
  /* ⚠ เกณฑ์คือ "ลำดับปลายทาง" ไม่ใช่ "ข้ามลำดับหรือไม่"
     เคยใช้เกณฑ์ข้ามลำดับ ผลคือเส้นจากปลัดถึงอธิบดี (ลำดับ 1 ไป 3) กลายเป็นเส้นประ
     ทั้งที่อธิบดีเป็นลูกโดยตรงของปลัดในผังนี้ ส่วนที่เป็นแค่ "อยู่ส่วนราชการเดียวกัน"
     คือลำดับ 5 กับ 6 เท่านั้น */
  /**
   * น้ำหนักเส้นสามระดับ ตามว่าเส้นนั้นบอกอะไร
   *
   *   เข้ม   ต่อลงลำดับถัดไปทันที — เป็นคู่ที่เจาะจง (อธิบดี→รองอธิบดีของกรมเดียวกัน)
   *   จาง   ข้ามลำดับ — ปลัดถึงอธิบดี ซึ่งต้องลากผ่านแถบลำดับ 2 ทั้งแถบ
   *   ประ   ลำดับ 5-6 — แปลว่า "อยู่ส่วนราชการเดียวกัน" เท่านั้น
   *
   * ⚠ ที่ต้องมีระดับ "จาง" เพราะเส้นข้ามลำดับต้องพาดผ่านการ์ดของแถบที่คั่นอยู่เสมอ
   *   ถ้าเข้มเท่ากับเส้นคู่เจาะจง มันจะเด่นกว่าเส้นที่มีความหมายจริง แล้วสายตา
   *   จะไปจับเส้นที่บอกแค่ว่า "อยู่ใต้ปลัดเหมือนกันทุกคน" ซึ่งไม่ได้บอกอะไรเลย
   */
  const tierOfPos = (id: string) => tierByPos.get(id) ?? 0;
  for (const [child, parent] of parentOf) {
    const sameDeptOnly = tierOfPos(child) >= 5;
    const skipsBand = !sameDeptOnly && tierOfPos(child) - tierOfPos(parent) > 1;
    edges.push({
      id: `${parent}->${child}`,
      source: parent,
      target: child,
      /**
       * ⚠ ต้องเป็นเส้นโค้ง (bezier) ไม่ใช่ smoothstep แบบผังหน่วยงาน
       *
       * smoothstep เดินเส้นแบบฉาก — ลงจากพ่อ วิ่งขวาง แล้วค่อยลงหาลูก
       * ผังหน่วยงานใช้ได้เพราะพ่อหนึ่งตัวมีลูกไม่กี่ตัวและอยู่แถวเดียว
       * แต่ที่นี่ปลัดมีลูก 23 ใบเรียงกว้าง 12 คอลัมน์ เส้นทุกเส้นจึงไปรวมกัน
       * เป็นรางแนวนอนเส้นเดียวพาดทั้งผัง แล้วแตกลงมาเป็นขาตั้ง
       * ผลคือดูไม่ออกว่าเส้นไหนไปหาใคร — ผู้ใช้เรียกว่า "เส้นเชื่อมกันมั่ว"
       *
       * เส้นโค้งออกจากจุดเดียวกันจะกางเป็นพัด แยกปลายทางได้ด้วยสายตา
       *
       * ⚠ ถ้าเปลี่ยนชนิดเส้นที่นี่ ต้องแก้ scripts/preview-people-map.tsx ด้วย
       *   สคริปต์นั้นวาดเส้นเอง ถ้าวาดคนละชนิดกับของจริง ภาพตรวจจะโกหก
       *   ซึ่งเกิดมาแล้วรอบหนึ่ง — ภาพตรวจวาด bezier แต่ของจริงเป็น smoothstep
       */
      type: 'tier',
      /* ⚠ แนวคร่อมคิดจาก "แถบของลำดับปลายทาง" ไม่ใช่จากแถวของการ์ดใบนั้น
         ดูเหตุผลเต็มที่ tierEdgePath — ลำดับที่ตัดสองแถวต้องห้อยจากคร่อมเส้นเดียวกัน
         ไม่งั้นอ่านเป็นสองลำดับ */
      data: { dir, bracket: (bandStart.get(tierOfPos(child)) ?? 0) - BRACKET_LIFT },
      style: sameDeptOnly
        ? { stroke: 'var(--line)', strokeWidth: 1, strokeDasharray: '4 5' }
        : skipsBand
          ? { stroke: 'var(--brand2)', strokeWidth: 1, opacity: 0.45 }
          : { stroke: 'var(--graph2)', strokeWidth: 1.4 },
    });
  }

  return { nodes, edges };
}

/**
 * เหลืออีกเท่าไรจะเกษียณ
 *
 * ⚠ retireInYears เป็นทศนิยม เขียนตรง ๆ จะได้ "เกษียณใน 0.05 ปี" บนการ์ด
 *   ซึ่งอ่านไม่ออกว่านานแค่ไหน และดูเหมือนระบบคำนวณพลาด
 *   ป้ายนี้ขึ้นเฉพาะคนที่เหลือไม่ถึงหนึ่งปี จึงต้องบอกเป็นเดือนเสมอ
 */
function retireIn(years: number): string {
  const months = Math.max(0, Math.round(years * 12));
  return months === 0 ? 'ปีนี้' : `${months} เดือน`;
}

function duration(months: number): string {
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y === 0) return `${m} เดือน`;
  if (m === 0) return `${y} ปี`;
  return `${y} ปี ${m} เดือน`;
}
